import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { updateUser } from 'api/users'
import { useAuthContext } from 'components/context/AuthProvider'
import FormCheckbox from 'components/form-elements/FormCheckbox'
import FormInput from 'components/form-elements/FormInput'
import FormPhone from 'components/form-elements/FormPhone'
import FormSelect from 'components/form-elements/FormSelect'
import ConfirmModal from 'components/modals/ConfirmModal'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, PHONE_IS_INVALID } from 'const'
import { useUnlicensedConfirm } from 'features/User/hooks/useUnlicensedConfirm'
import {
  getPathwayNameById,
  isFamilyCaregiverPathway,
  isNotEnglishPathway,
} from 'models/pathways.model'
import { educationOptions, preferredLanguageOptions, yearsOfExperience } from 'models/user.model'
import { useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import {
  AccessCodeDropdownItem,
  EUserEducation,
  EUserExperience,
  EUserLanguage,
  Nullish,
  Pathway,
  TableResponse,
  UserModel,
  UserUpdateRequest,
} from 'types'
import { boolean, coerce, literal, object, string, TypeOf, ZodIssueCode } from 'zod'

const formSchema = object({
  firstname: string().min(1, FIELD_IS_REQUIRED),
  lastname: string().min(1, FIELD_IS_REQUIRED),
  email: string().email(EMAIL_IS_INVALID),
  accessCodeId: string(),
  shouldLeaveOrganization: boolean(),
  phoneNumber: string().min(10, PHONE_IS_INVALID).or(literal('')).optional(),
  isSmsNotificationEnabled: boolean(),
  customTrainingPathwayTypeId: coerce.number(),
  education: string().min(1, 'Education is required'),
  experience: string().min(1, FIELD_IS_REQUIRED),
  language: string().min(1, FIELD_IS_REQUIRED),
}).superRefine((data, ctx) => {
  if (data.isSmsNotificationEnabled && (!data.phoneNumber || data.phoneNumber.length < 10)) {
    ctx.addIssue({
      path: ['phoneNumber'],
      code: ZodIssueCode.custom,
      message: FIELD_IS_REQUIRED,
    })
  }
})

type FormType = TypeOf<typeof formSchema>

export function UserOrganizationDetailsForm({
  user,
  pathways,
  accessCodes,
  onAfterUpdate,
}: {
  user: UserModel
  pathways: TableResponse<Pathway>
  accessCodes: AccessCodeDropdownItem[]
  onAfterUpdate: () => void
}) {
  const { user: currentUser } = useAuthContext()

  const isSameOrganization = useMemo(() => {
    return user.organizationId === currentUser?.organizationId
  }, [user, currentUser])

  const defaultValues = {
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
    accessCodeId: user.isUnlicensed ? 'null' : String(user.accessCodeId),
    shouldLeaveOrganization: !!user.leftOrganizationAt,
    phoneNumber: user.phoneNumber,
    isSmsNotificationEnabled: user.isSmsNotificationEnabled,
    customTrainingPathwayTypeId: user.trainingPathwayTypeId,
    education: user.education,
    experience: user.experience,
    language: user.language,
  }

  const methods = useForm<FormType>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const saveUserMutation = useMutation(updateUser)

  const getRequestData = useCallback(() => {
    const values = methods.getValues()
    const accessCodeId =
      values.accessCodeId === 'null' ? user.accessCodeId : parseInt(values.accessCodeId)

    const customTrainingPathwayTypeId = Number(values.customTrainingPathwayTypeId)
    let language = values.language as EUserLanguage
    if (
      isNotEnglishPathway(customTrainingPathwayTypeId) &&
      values.language === EUserLanguage.english
    ) {
      language = EUserLanguage.other
    }

    const data: UserUpdateRequest = {
      id: user.id,
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      accessCodeId,
      shouldLeaveOrganization: values.shouldLeaveOrganization,
      isUnlicensed: values.accessCodeId === 'null',
      phoneNumber: values.phoneNumber,
      customTrainingPathwayTypeId,
      education: values.education as EUserEducation,
      experience: values.experience as EUserExperience,
      language,
    }

    return data
  }, [methods, user.id, user.accessCodeId])

  const handleUpdate = useCallback(async () => {
    const request = getRequestData()
    await saveUserMutation.mutateAsync(request)
    NotificationSys.showSuccess('User updated')
    onAfterUpdate()
  }, [getRequestData, onAfterUpdate, saveUserMutation])

  const pathwayOptions = useMemo(() => {
    // We should not show Family Caregivers pathway in the dropdown,
    const traineePathways = pathways.data.rows.filter((pathwayItem) => {
      return !isFamilyCaregiverPathway(pathwayItem.trainingPathwayTypeId)
    })

    return traineePathways.map((pathwayItem) => {
      return {
        label: pathwayItem.name,
        value: pathwayItem.trainingPathwayTypeId,
      }
    })
  }, [pathways])

  const isLanguageDisabled = useMemo(() => {
    return !isNotEnglishPathway(user.trainingPathwayTypeId)
  }, [user])

  const languageOptions = useMemo(() => {
    if (isNotEnglishPathway(user.trainingPathwayTypeId)) {
      return preferredLanguageOptions.filter((item) => item.value !== EUserLanguage.english)
    }

    return preferredLanguageOptions
  }, [user])

  const accessCodeOptions = useMemo(() => {
    const options = accessCodes.map(({ id, code, specificState, specificUnitName }) => ({
      value: id,
      label: `${specificState}/${specificUnitName}/${code}`,
    }))

    if (
      user.accessCode &&
      !user.isUnlicensed &&
      !options.find((o) => o.value === user.accessCode?.id)
    ) {
      const { id, code, specificState, specificUnitName } = user.accessCode
      options.unshift({
        value: id,
        label: `${specificState}/${specificUnitName}/${code}`,
      })
    }

    return [
      {
        value: 'null',
        label: 'Unlicensed',
      },
      ...options,
    ]
  }, [accessCodes, user])

  const { unlicensedConfirm, openUnlicensedConfirm } = useUnlicensedConfirm()

  const getPathwayName = useCallback(
    (pathwayTypeId?: number) => {
      return getPathwayNameById(pathways, pathwayTypeId)
    },
    [pathways],
  )

  const [isOpenConfirm, setIsOpenConfirm] = useState(false)
  const [checkResult, setCheckResult] = useState<Nullish<UserUpdateRequest>>(null)
  const handleCloseConfirm = useCallback(() => setIsOpenConfirm(false), [])

  const confirmMessage = useMemo(() => {
    if (!checkResult) {
      return ''
    }

    const oldName = getPathwayName(user.trainingPathwayTypeId)
    const request = getRequestData()
    const newName = getPathwayName(Number(request.customTrainingPathwayTypeId))

    return `After saving these changes the trainee's pathway type will be changed from ${oldName} to ${newName}. Please confirm the action.`
  }, [checkResult, getPathwayName, getRequestData, user])

  const handleSubmit = useCallback(async () => {
    const request = getRequestData()

    if (request.isUnlicensed && !!user.accessCode?.id && !user.isUnlicensed) {
      if (!(await openUnlicensedConfirm())) {
        return
      }
    }

    const isChangedPathway = request.customTrainingPathwayTypeId !== user.trainingPathwayTypeId
    if (isChangedPathway) {
      setCheckResult(request)
      setIsOpenConfirm(true)
    } else {
      handleUpdate()
    }
  }, [getRequestData, handleUpdate, openUnlicensedConfirm, user])

  return (
    <>
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(handleSubmit)}
          noValidate
          width="100%"
          mt={4}
        >
          <Grid container spacing={4}>
            <Grid item sm={12} md={12} lg={6} xl={6}>
              <Stack spacing={2}>
                <FormInput
                  label="First Name"
                  name="firstname"
                  required
                  fullWidth
                  disabled={!isSameOrganization}
                />
                <FormInput
                  label="Last Name"
                  name="lastname"
                  required
                  fullWidth
                  disabled={!isSameOrganization}
                />
                <FormPhone
                  label="Mobile phone number"
                  name="phoneNumber"
                  fullWidth
                  disabled={!isSameOrganization}
                />
                <FormInput label="Email" name="email" fullWidth disabled />
              </Stack>

              <Stack spacing={2} mt={2}>
                <FormSelect
                  label="State/Unit"
                  name="accessCodeId"
                  required={!user.leftOrganizationAt}
                  options={accessCodeOptions}
                  disabled={!!user.leftOrganizationAt || !isSameOrganization}
                  maxRows={5}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                />
                <FormCheckbox
                  name="shouldLeaveOrganization"
                  label="Left the Organization"
                  confirmText="Please confirm that the trainee has left the organization."
                  disabled={!!user.leftOrganizationAt || !isSameOrganization}
                />
              </Stack>

              <Stack mt={4}>
                <Typography fontWeight={600}>Pathway changes</Typography>
                <Typography fontSize={14} sx={{ opacity: '0.6' }}>
                  Please note that modifying the fields below may potentially alter the pathway for
                  trainees
                </Typography>
              </Stack>

              <Stack spacing={2} mt={2}>
                <FormSelect
                  label="Pathway Type"
                  name="customTrainingPathwayTypeId"
                  required
                  options={pathwayOptions}
                  disabled={!isSameOrganization}
                />

                <FormSelect
                  label="How much education did you complete?"
                  name="education"
                  required
                  disabled
                  options={educationOptions}
                />

                <FormSelect
                  label="Years of experience as a professional caregiver"
                  name="experience"
                  required
                  disabled
                  options={yearsOfExperience}
                />

                <FormSelect
                  label="What is your preferred language?"
                  name="language"
                  required
                  disabled={isLanguageDisabled || !isSameOrganization}
                  options={languageOptions}
                />
              </Stack>
            </Grid>

            <Grid item sm={12} md={12} lg={12} xl={12}>
              <LoadingButton
                disabled={saveUserMutation.isLoading || !isSameOrganization}
                loading={saveUserMutation.isLoading}
                variant="contained"
                type="submit"
                sx={{ width: '120px' }}
              >
                Save
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>

        {isOpenConfirm && (
          <ConfirmModal
            title="Confirm Pathway Changes"
            body={confirmMessage}
            handleClose={handleCloseConfirm}
            handleConfirm={handleUpdate}
            loading={saveUserMutation.isLoading}
          />
        )}

        {unlicensedConfirm}
      </FormProvider>
    </>
  )
}
